import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, ArrowRight, Calendar, Zap, BookOpen } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function PortalDashboard() {
  const { user } = useAuth();
  const [feed, setFeed] = useState({ posts: [], groups: [] });
  const [allGroups, setAllGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [f, g] = await Promise.all([
          api.get("/portal/feed"),
          api.get("/portal/groups"),
        ]);
        setFeed(f.data);
        setAllGroups(g.data);
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const myGroups = allGroups.filter((g) => g.my_status === "approved" || g.my_role === "leader");
  const pendingGroups = allGroups.filter((g) => g.my_status === "pending");

  return (
    <div data-testid="portal-dashboard">
      <div className="mb-10">
        <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-3">Welcome back</div>
        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
          Hello, <span className="italic text-electric">{user?.name?.split(" ")[0]}.</span>
        </h1>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><div className="w-8 h-8 border-2 border-electric border-t-transparent rounded-full animate-spin" role="status" aria-label="Loading" /></div>
      ) : (
        <>
          {/* MY GROUPS */}
          <section className="mb-12">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display font-bold text-2xl">My Groups</h2>
              <Link to="/portal/groups" className="text-sm text-electric font-bold hover:gap-2 inline-flex items-center gap-1.5 transition-all" data-testid="dashboard-browse-groups">
                Browse all <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            {myGroups.length === 0 ? (
              <div className="p-10 rounded-2xl border border-white/5 bg-[#0a0a0a] text-center">
                <Users className="w-10 h-10 text-electric mx-auto mb-4" aria-hidden="true" />
                <h3 className="font-display font-bold text-xl mb-2">You haven't joined any groups yet</h3>
                <p className="text-zinc-400 text-sm mb-5">Browse the list and request to join the ones that fit.</p>
                <Link to="/portal/groups" className="inline-flex items-center gap-2 bg-electric text-black px-5 py-2.5 rounded-full font-bold text-sm hover:bg-[#4ade80] transition" data-testid="dashboard-cta-explore">
                  Explore Groups
                </Link>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {myGroups.map((g) => (
                  <Link key={g.id} to={`/portal/groups/${g.slug}`} className="group p-6 rounded-2xl border border-white/5 bg-[#0a0a0a] hover:border-electric/50 transition" data-testid={`dashboard-group-${g.slug}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl" aria-hidden="true">{g.icon || "✨"}</span>
                      {g.my_role === "leader" && <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-electric/10 text-electric border border-electric/20 font-bold">Leader</span>}
                    </div>
                    <div className="font-display font-bold text-lg mb-1 group-hover:text-electric transition">{g.name}</div>
                    <div className="text-xs text-zinc-500">{g.member_count} {g.member_count === 1 ? "member" : "members"}</div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {pendingGroups.length > 0 && (
            <section className="mb-12 p-5 rounded-2xl border border-yellow-500/20 bg-yellow-500/5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-yellow-400 font-bold mb-3">Pending Approval</div>
              <p className="text-sm text-zinc-300 mb-3">You're waiting on approval to join:</p>
              <div className="flex flex-wrap gap-2">
                {pendingGroups.map((g) => (
                  <span key={g.id} className="text-sm text-zinc-200 px-3 py-1 rounded-full bg-white/5 border border-white/10">{g.icon} {g.name}</span>
                ))}
              </div>
            </section>
          )}

          {/* FEED */}
          <section>
            <h2 className="font-display font-bold text-2xl mb-5">Recent in your groups</h2>
            {feed.posts.length === 0 ? (
              <div className="p-10 rounded-2xl border border-white/5 bg-[#0a0a0a] text-center text-zinc-400 text-sm">
                No posts yet. When your group leaders share something, it'll show up here.
              </div>
            ) : (
              <div className="space-y-4">
                {feed.posts.slice(0, 8).map((p) => (
                  <motion.div key={p.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-6 rounded-2xl border border-white/5 bg-[#0a0a0a]" data-testid={`feed-post-${p.id}`}>
                    <div className="flex items-center gap-3 text-xs uppercase tracking-wider mb-3">
                      <span className="px-2 py-0.5 rounded-full bg-electric/10 text-electric border border-electric/20 font-bold">{p.group_name}</span>
                      <span className="text-zinc-500">{p.category}</span>
                      <span className="text-zinc-600 ml-auto">{fmt(p.created_at)}</span>
                    </div>
                    <h3 className="font-display font-bold text-xl mb-2">{p.title}</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed line-clamp-3 whitespace-pre-wrap">{p.body}</p>
                    {p.group_id && (
                      <Link to={`/portal/groups/${feed.groups.find((g) => g.id === p.group_id)?.slug || ""}`} className="mt-3 inline-flex items-center gap-1.5 text-electric text-sm font-bold hover:gap-2 transition-all">
                        Open group <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
